import { cn } from '@cobliteam/ui/utils';
import type { Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect } from 'react';

export interface ShieldCheckIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface ShieldCheckIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const pathVariants: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      opacity: { duration: 0.1 },
    },
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    scale: [0.5, 1],
    transition: {
      duration: 0.4,
      opacity: { duration: 0.1 },
    },
  },
};

const ShieldCheckIcon = forwardRef<ShieldCheckIconHandle, ShieldCheckIconProps>(({ className, size = 28, isHovered, ...props }, _) => {
  const controls = useAnimation();

  useEffect(() => {
    if (isHovered) {
      controls.start('animate');
    } else {
      controls.start('normal');
    }
  }, [isHovered, controls]);

  return (
    <div className={cn(className)} {...props}>
      <svg
        data-testid='lucide-shield-check'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' />
        <motion.path variants={pathVariants} initial='normal' animate={controls} d='m9 12 2 2 4-4' />
      </svg>
    </div>
  );
});

ShieldCheckIcon.displayName = 'ShieldCheckIcon';

export { ShieldCheckIcon };
